import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  retryWhen,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { AuthService } from '@personal-area/global/authorization/services';
import { LoadingState } from '@personal-area/global/state';
import { retryStrategy } from '@personal-area/global/http/services';
import { EventType, GA4Event, GTMService } from '@personal-area/global/gtm';

import { ProfileVariant } from '../../models/profile-variant.dto';
import { ABTestApiService } from '../../services/ab-test-api.service';
import { GetTests, GetTestsFail, GetTestsSuccess } from '../actions/ab-test';
import { ABTestFacade } from '../facades/ab-test';

const templateGTMEvent: GA4Event = {
  context: 'abTest',
  action: 'viewed',
  object: 'abtest',
};

@Injectable()
export class ABTestEffects {
  constructor(
    private _actions$: Actions,
    private _abTestsApiService: ABTestApiService,
    private _gtmService: GTMService,
    private _authService: AuthService,
    private _abTestFacade: ABTestFacade,
  ) {}

  getTests$ = createEffect(() =>
    this._actions$.pipe(
      ofType(GetTests),
      withLatestFrom(this._abTestFacade.loadingState$, (_, loadingState) =>
        [LoadingState.loading, LoadingState.failed].includes(loadingState),
      ),
      filter(Boolean),
      switchMap(() =>
        this._abTestsApiService.getProfile().pipe(
          tap(({ variants }) => {
            if (!variants.length) {
              this._sendTestFallback();
              return;
            }

            this._sendAllTests(variants);
          }),
          map((tests) => GetTestsSuccess(tests)),
          retryWhen(retryStrategy()),
          catchError((error) => {
            this._sendTestError(error);
            return of(GetTestsFail(error));
          }),
        ),
      ),
    ),
  );

  private _sendTestFallback(): void {
    this._gtmService.event(EventType.ABTestViewed, <GA4Event>{
      ...templateGTMEvent,
      test_name: '',
      group: '',
      parameters: '',
      user_id_from_request: this._authService.userId.toString(),
    });
  }

  private _sendAllTests(variants: ProfileVariant[]): void {
    variants.forEach(({ groupID, name }) => {
      this._gtmService.event(EventType.ABTestViewed, <GA4Event>{
        ...templateGTMEvent,
        test_name: name,
        group: groupID,
        parameters: '',
        user_id_from_request: this._authService.userId.toString(),
      });
    });
  }

  private _sendTestError(error: HttpErrorResponse): void {
    const parameters: Record<string, string> = {
      errorMessage: error?.message?.slice(80) || '',
    };

    this._gtmService.event(EventType.ABTestViewed, <GA4Event>{
      ...templateGTMEvent,
      test_name: 'error',
      group: '',
      parameters: JSON.stringify(parameters),
      user_id_from_request: this._authService.userId.toString(),
    });
  }
}
